// EMİR #78 — "İlk Adımlar" kartı: yeni kullanıcıyı döngüye sokan kapatılabilir
// checklist (home feed üstünde). 4 adım mevcut PUBLIC sinyallerden işaretlenir:
//   ☐ profil fotoğrafı  ☐ ilk 5★  ☐ ilk check-in  ☐ ilk MyClip
// Tamamlanınca kutlama → kart kaybolur (localStorage dismiss). Gizli eşik/oran YOK.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import "./FirstStepsCard.css";

const K_DISMISS = (uid: string) => `mlsa.firstSteps.dismiss:${uid}`;
const K_RATED = (uid: string) => `mlsa.firstSteps.rated:${uid}`;
const K_CHECKIN = (uid: string) => `mlsa.firstSteps.checkin:${uid}`;

function lsGet(k: string): boolean { try { return localStorage.getItem(k) === "1"; } catch { return false; } }
function lsSet(k: string): void { try { localStorage.setItem(k, "1"); } catch { /* no-op */ } }

interface Props { uid?: string; onCompose?: () => void }

const FirstStepsCard: React.FC<Props> = ({ uid, onCompose }) => {
  const { t } = useTranslation(["mycards"]);
  const [dismissed, setDismissed] = useState(true);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [hasClip, setHasClip] = useState(false);
  const [rated, setRated] = useState(false);
  const [checkin, setCheckin] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  // EMİR #80-D.14 — varsayılan DARALTILMIŞ pill; dokununca açılır (oturum-içi
  // state; kalıcı dismiss ayrı). Kutlama gelirse otomatik açılır (patlama görünsün).
  const [expanded, setExpanded] = useState(false);

  // İlk mount: dismiss + localStorage bayrakları oku.
  useEffect(() => {
    if (!uid) { setDismissed(true); return; }
    setDismissed(lsGet(K_DISMISS(uid)));
    setRated(lsGet(K_RATED(uid)));
    setCheckin(lsGet(K_CHECKIN(uid)));
  }, [uid]);

  // User doc canlı: profil fotoğrafı + ilk MyClip/gönderi.
  useEffect(() => {
    if (!uid) return undefined;
    const ref = doc(db, "users", uid);
    const unsub = onSnapshot(ref, (snap) => {
      const u: any = snap.exists() ? snap.data() : {};
      setHasPhoto(!!(u.profilFoto || u.photoURL || u.avatar));
      setHasClip(Number(u.v3_clipCount || 0) > 0 || Number(u.postsCount || 0) > 0);
    }, () => { /* sessiz */ });
    return () => unsub();
  }, [uid]);

  // Değer-anı olayları → ilk 5★ / ilk check-in bayrağı (kalıcı).
  useEffect(() => {
    if (!uid) return undefined;
    const onRated = (e: any) => { if (Number(e?.detail?.value) >= 4) { lsSet(K_RATED(uid)); setRated(true); } };
    const onCheckin = () => { lsSet(K_CHECKIN(uid)); setCheckin(true); };
    window.addEventListener("mlv3:clip-rated", onRated as any);
    window.addEventListener("mylasa:checkin", onCheckin as any);
    return () => {
      window.removeEventListener("mlv3:clip-rated", onRated as any);
      window.removeEventListener("mylasa:checkin", onCheckin as any);
    };
  }, [uid]);

  const steps = useMemo(() => ([
    { key: "photo", done: hasPhoto, label: t("mycards:firstSteps.photo"), emoji: "🖼️" },
    { key: "rate", done: rated, label: t("mycards:firstSteps.rate"), emoji: "⭐" },
    { key: "checkin", done: checkin, label: t("mycards:firstSteps.checkin"), emoji: "📍" },
    { key: "clip", done: hasClip, label: t("mycards:firstSteps.clip"), emoji: "🎬" },
  ]), [hasPhoto, rated, checkin, hasClip, t]);

  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  // Hepsi tamamlanınca: kısa kutlama → kalıcı dismiss.
  useEffect(() => {
    if (!uid || dismissed) return undefined;
    if (allDone) {
      setCelebrate(true);
      const t = setTimeout(() => { lsSet(K_DISMISS(uid)); setDismissed(true); }, 2600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [allDone, dismissed, uid]);

  const close = useCallback(() => {
    if (uid) lsSet(K_DISMISS(uid));
    setDismissed(true);
  }, [uid]);

  if (!uid || dismissed) return null;

  // EMİR #80-D.13 — İLERLEME SVG HALKASI (doneCount dolgu; Gold Star altın kimlik).
  // Tek çizim fonksiyonu: pill'de mini (18px), açık kartta büyük (26px).
  const ring = (size: number) => {
    const r = (size - 4) / 2;
    const C = 2 * Math.PI * r;
    const frac = steps.length ? doneCount / steps.length : 0;
    return (
      <svg className="fsc-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle className="fsc-ring-bg" cx={size / 2} cy={size / 2} r={r} strokeWidth="3" fill="none" />
        <circle
          className="fsc-ring-fill" cx={size / 2} cy={size / 2} r={r} strokeWidth="3" fill="none"
          strokeDasharray={`${(C * frac).toFixed(1)} ${C.toFixed(1)}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
    );
  };

  // EMİR #80-D.14 — DARALTILMIŞ PILL (varsayılan): "İlk Adımlar 2/4" + mini halka.
  if (!expanded && !celebrate) {
    return (
      <button
        type="button"
        className="fsc-pill pressable"
        onClick={() => setExpanded(true)}
        aria-expanded={false}
        aria-label={`${t("mycards:firstSteps.title")} ${doneCount}/${steps.length}`}
      >
        {ring(18)}
        <span className="fsc-pill-title">{t("mycards:firstSteps.title")}</span>
        <span className="fsc-pill-count">{doneCount}/{steps.length}</span>
      </button>
    );
  }

  return (
    <div className={`fsc-card${celebrate ? " fsc-card--done" : ""}`} role="region" aria-label="İlk Adımlar">
      <button type="button" className="fsc-close pressable" onClick={close} aria-label="Kapat">×</button>
      {/* Başlık satırı: tıkla → tekrar daralt (kutlamada kilitli — patlama görünsün) */}
      <div
        className="fsc-head fsc-head--tap"
        onClick={() => { if (!celebrate) setExpanded(false); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !celebrate) setExpanded(false); }}
        aria-expanded
      >
        {ring(26)}
        <span className="fsc-title">{celebrate ? t("mycards:firstSteps.done") : t("mycards:firstSteps.title")}</span>
        <span className="fsc-progress" aria-hidden="true">{doneCount}/{steps.length}</span>
      </div>
      {!celebrate && (
        <p className="fsc-motiv">{t("mycards:firstSteps.motiv")}</p>
      )}
      <ul className="fsc-list">
        {steps.map((s) => (
          <li key={s.key} className={`fsc-item${s.done ? " fsc-item--done" : ""}`}>
            <span className="fsc-check" aria-hidden="true">{s.done ? "✓" : ""}</span>
            <span className="fsc-emoji" aria-hidden="true">{s.emoji}</span>
            <span className="fsc-label">{s.label}</span>
            {!s.done && s.key === "clip" && onCompose && (
              <button type="button" className="fsc-go pressable" onClick={onCompose}>{t("mycards:firstSteps.go")}</button>
            )}
          </li>
        ))}
      </ul>
      {/* EMİR #80-D.14 — kutlamada ALTIN PARÇACIK patlaması (mantık değişmedi:
          celebrate 2.6s sonra kalıcı dismiss). transform/opacity-only, ≤12 DOM. */}
      {celebrate && (
        <span className="fsc-burst" aria-hidden="true">
          <i /><i /><i /><i /><i /><i /><i /><i /><i /><i />
        </span>
      )}
    </div>
  );
};

export default FirstStepsCard;
